import React, { useState, useEffect, useMemo, useCallback, useRef, memo, useTransition } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Paper,
  Chip,
  Autocomplete,
  FormControlLabel,
  Switch,
  alpha,
  Alert,
  Collapse,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { useNotification, notifyPermissionError } from '@1823-partners/core';
import { ExpandMore as ExpandMoreIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import BlotterGrid from './BlotterGrid';
import {
  isAdcoGeneratedVector,
  withoutAdcoGeneratedVectors,
} from '../utils/adcoDisplayUtils';

import {
  fetchSecurityFormFields,
  validateSecurityForm,
  uploadCdi,
  uploadCashflow,
  uploadRamp,
  fetchCustomRamps,
  fetchDeals,
  fetchOptionRedemptionValues,
  loadDeal,
  fetchBlotter
} from '../api';

// Date-only helpers: the backend stores date fields as 'YYYY-MM-DD' (no time).
// Build/serialize the DatePicker value in the LOCAL frame so the displayed
// calendar day matches the stored string instead of shifting a day west of UTC.
// (new Date('YYYY-MM-DD') parses as UTC midnight; toISOString() re-applies UTC.)
const dateOnlyToPickerValue = (val) => {
  if (!val) return null;
  // Anchor a 'YYYY-MM-DD' string at local midnight so the day isn't shifted.
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return new Date(`${val}T00:00:00`);
  }
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const pickerValueToDateOnly = (d) => {
  if (!d || isNaN(d.getTime())) return '';
  // Use local date components (not toISOString) to keep the displayed day.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const DEFAULT_OPTION_REDEMPTION_BY_CHOICES = [
  'ASAP',
  'CLOSING',
  'REINVESTEND',
  'SETTLE',
  'NEVER',
];

const hasFieldValue = value => value !== '' && value !== null && value !== undefined;

// "Run to call" is not a separate INTEX input. A blank Option Redemption Name
// with Option Redemption By = ASAP makes the server emit
// OPTREDEEM_CALL_WHEN[deal::*]=ASAP, which exercises every optional redemption
// at its first allowed date so yield and WAL price to call.
const RUN_TO_CALL_VALUES = { OPTIONREDEMPTNAME: '', OptionRedemptionBy: 'ASAP', OptionRedemptionValue: '' };
const RUN_TO_CALL_HELP = "Exercise the deal's optional redemption at the first allowed date so yield and WAL price to call. Sets Option Redemption By to ASAP and clears the redemption name.";

export const isRunToCall = data => (
  data?.OptionRedemptionBy === 'ASAP'
  && !hasFieldValue(data?.OptionRedemptionValue)
  && !hasFieldValue(data?.OPTIONREDEMPTNAME)
);

// Server fields the form renders under different ids (split inputs).
const SERVER_FIELD_ALIASES = { OPTIONREDEMPTVAL: ['OptionRedemptionBy', 'OptionRedemptionValue'] };

// Turn /security/validate's response into { byId, summary }: byId marks the
// offending inputs, summary feeds the banner and the notification. Prefers the
// structured field_errors list; falls back to parsing the "Label: message"
// strings, and still understands the legacy "field 'ID': message" shape.
export const mapValidationErrors = (result, fields) => {
  const labelToId = new Map(
    (fields || []).map(field => [String(field.label || '').trim().toLowerCase(), field.id])
  );
  const byId = {};
  const messages = [];
  const mark = (id, message) => {
    if (!id) return;
    byId[id] = message;
    (SERVER_FIELD_ALIASES[id] || []).forEach(alias => { byId[alias] = message; });
  };
  const structured = Array.isArray(result?.field_errors) ? result.field_errors : [];
  if (structured.length) {
    structured.forEach(({ field, id, message }) => {
      const text = message || String(field || '');
      mark(id || labelToId.get(String(field || '').trim().toLowerCase()), text);
      messages.push(field ? `${field}: ${text}` : text);
    });
  } else {
    (Array.isArray(result?.errors) ? result.errors : []).forEach(raw => {
      const text = String(raw);
      const legacy = text.match(/field '(\w+)'[:\s]*(.*)/);
      const labeled = text.match(/^([^:]+):\s*(.+)$/);
      if (legacy) {
        mark(legacy[1], legacy[2] || text);
        messages.push(`${legacy[1]}: ${legacy[2] || text}`);
      } else if (labeled) {
        mark(labelToId.get(labeled[1].trim().toLowerCase()), labeled[2].trim());
        messages.push(text);
      } else {
        messages.push(text);
      }
    });
  }
  return {
    byId,
    summary: messages.join('; ') || 'Validation failed. Please check your input.',
  };
};

const normalizeArrayResponse = responseData => {
  let parsed = responseData;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }

  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed?.data)) return parsed.data;
  if (Array.isArray(parsed?.results)) return parsed.results;
  return [];
};

// Memoize field styles to prevent recreation on every render
const useFieldStyles = (theme) => {
  return useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: theme.palette.background.paper,
      fontSize: '15px',
      minHeight: 48,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.mode === 'dark' 
          ? alpha(theme.palette.common.white, 0.23)
          : 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.mode === 'dark' 
          ? alpha(theme.palette.common.white, 0.4)
          : 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover': {
        bgcolor: theme.palette.mode === 'dark' 
          ? alpha(theme.palette.background.paper, 0.9)
          : '#f9fafb'
      },
      '&.Mui-focused': {
        bgcolor: theme.palette.background.paper,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
          borderWidth: 2,
        },
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
      },
      '&.Mui-error': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.error.main,
        },
        '&.Mui-focused': {
          boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.1)}`
        }
      }
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontWeight: 500,
      fontSize: '16px',
      '&.Mui-focused': {
        color: theme.palette.primary.main
      },
      '&.Mui-error': {
        color: theme.palette.error.main
      },
      '&.MuiInputLabel-shrink': {
        color: theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.7)
          : theme.palette.text.secondary,
        '&.Mui-focused': {
          color: theme.palette.primary.main
        },
        '&.Mui-error': {
          color: theme.palette.error.main
        }
      }
    },
    '& .MuiFormHelperText-root': {
      fontSize: '12px',
      marginLeft: 0,
      marginTop: 1
    },
    '& .MuiSelect-select': {
      paddingTop: '12px',
      paddingBottom: '12px'
    },
    '& .MuiAutocomplete-root': {
      '& .MuiOutlinedInput-root': {
        paddingTop: '4px',
        paddingBottom: '4px'
      }
    }
  }), [theme]);
};

export default function AddSecurityModal({ open, onClose, userId, modifiedDate, editData, onSuccess, fullScreen = false, embedded = false, comparisonMode = false, selectedTranches: externalTranches = [], initialPricingTargets = null, initialShockBps = null }) {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fieldStyles = useFieldStyles(theme);
  const [isPending, startTransition] = useTransition();
  
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [validationError, setValidationError] = useState(''); // For validation error bar
  const validationBannerRef = useRef(null);
  // Every validation failure lands in three places: the banner, a toast, and
  // (via setErrors at the call site) the offending field. The banner sits at
  // the top of the form while the submit button is at the bottom, so bring it
  // into view — the toast covers the case where the user does not scroll.
  const showValidationFailure = useCallback(message => {
    setValidationError(message);
    showNotification(message, 'error');
    validationBannerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [showNotification]);
  const [blotterData, setBlotterData] = useState([]);
  const [files, setFiles] = useState({ cdi: null, cashflow: null, ramp: null });
  const [loading, setLoading] = useState(false);
  const [blotterLoading, setBlotterLoading] = useState(false); // Separate loading state for blotter
  const [choicesLoading, setChoicesLoading] = useState(false); // Tranche/CUSIP dropdown choices loading from /deal
  const [fieldsLoaded, setFieldsLoaded] = useState(false); // Track if fields have been loaded
  const [dealPassword, setDealPassword] = useState(''); // Password for loading deal
  const [intexDeal, setIntexDeal] = useState(''); // Intex deal ID for loading deal
  // Pricing targets for comparison mode - initialize from props if editing
  const [cmpPrices, setCmpPrices] = useState(() => {
    if (!initialPricingTargets) return {};
    const p = {};
    Object.entries(initialPricingTargets).forEach(([t, v]) => { if (v?.price != null) p[t] = v.price; });
    return p;
  });
  const [cmpYields, setCmpYields] = useState(() => {
    if (!initialPricingTargets) return {};
    const y = {};
    Object.entries(initialPricingTargets).forEach(([t, v]) => { if (v?.yield != null) y[t] = v.yield; });
    return y;
  });
  const [cmpSpreads, setCmpSpreads] = useState(() => {
    if (!initialPricingTargets) return {};
    const s = {};
    Object.entries(initialPricingTargets).forEach(([t, v]) => { if (v?.spread != null) s[t] = v.spread; });
    return s;
  });
  const [cmpShockBps, setCmpShockBps] = useState(initialShockBps || '');
  const processedEditDataRef = useRef(null); // Track processed editData to prevent duplicates
  const initialEditCusipsRef = useRef(null); // Track initial CUSIPs from editData
  const userModifiedTrancheRef = useRef(false); // Track if user manually changed tranches
  const baseFormDataRef = useRef(null); // Track initial form data for diff display in embedded mode
  const loadedEditFormDataRef = useRef(null); // Form state as editData loaded it, before the ASOFDT default
  const optionRedemptionNamesLoadingRef = useRef(false);
  
  // Preloaded data maps for instant access
  const [preloadedData, setPreloadedData] = useState({
    deals: [],
    optionRedemptionNames: [],
    optionRedemptionNamesLoaded: false,
    trancheCusipMap: {}, // { dealName: { tranche1: [cusip1, cusip2], tranche2: [cusip3] } }
  });
  
  // Use ref to access latest trancheCusipMap without causing re-renders
  const trancheCusipMapRef = useRef(preloadedData.trancheCusipMap);
  
  // Keep ref in sync with state
  useEffect(() => {
    trancheCusipMapRef.current = preloadedData.trancheCusipMap;
  }, [preloadedData.trancheCusipMap]);

  // Field transformation functions
  // label->id map for edit
  const labelToId = useMemo(
    () => Object.fromEntries(fields.map(f => [f.label, f.id])),
    [fields]
  );

  const openFormTransform = useCallback((rowData) => {
    const transformed = {};
    const ramps = ['CPR', 'CDR', 'Draw Rate'];
    
    Object.entries(rowData).forEach(([name, value]) => {
      if (value === '' || value === null || value === undefined || name === 'Password'
          || name.includes('Custom Ramp') || name === 'Borrower Benefit Other') {
        return;
      }
      
      if (name === 'Option Redemption Value') {
        if (value) {
          const vals = String(value).split('+');
          transformed['OptionRedemptionBy'] = vals[0]?.toUpperCase() || '';
          if (vals.length > 1) {
            transformed['OptionRedemptionValue'] = parseFloat(vals[1]) || 0;
          }
        }
      } else if (ramps.includes(name)) {
        if (value !== '' && value !== null && value !== undefined) {
          const stringValue = String(value);
          if (isAdcoGeneratedVector(name, stringValue, rowData)) {
            // Keep generated monthly vectors intact in form state. The field
            // renderer replaces the raw value with a read-only ADCO label and
            // exposes this vector through its tooltip.
            transformed[labelToId[name] || name] = stringValue;
          } else if (!stringValue[0]?.match(/\d/)) {
            // Doesn't start with digit - use default 100 and set ramp type
            transformed[name] = 100;
            const rampPrefix = name === 'Draw Rate' ? 'DrawRate' : name;
            transformed[`${rampPrefix}RampType`] = value;
            transformed[`${rampPrefix}RampValue`] = 0;
          } else {
            // Starts with digit - split on 'using'
            const vals = stringValue.split('using');
            const percentage = vals[0]?.replace('%', '').trim() || '0';
            transformed[name] = parseFloat(percentage) || 0;
            if (vals.length > 1) {
              const rampPrefix = name === 'Draw Rate' ? 'DrawRate' : name;
              transformed[`${rampPrefix}RampType`] = vals[1]?.trim() || 'LINEAR';
              transformed[`${rampPrefix}RampValue`] = 0; // Default value since not specified
            }
          }
        }
      } else if (name === 'Borrower Benefit ACH') {
        if (value) {
          const vals = String(value).split('/');
          const getNumber = (str) => {
            const num = parseFloat(str);
            return isNaN(num) ? 0 : num;
          };
          transformed['BorrowerBenefitType'] = vals[0]?.toUpperCase() || 'ACH';
          transformed['BorrowerBenefitValue'] = getNumber(vals[0]);
        }
      }
      // Handle BORROWERBENEFIT field (backend field name)
      else if (name === 'BORROWERBENEFIT') {
        if (value) {
          const vals = String(value).split('+');
          transformed['BorrowerBenefitType'] = vals[0]?.toUpperCase() || 'ACH';
          if (vals.length > 1) {
            transformed['BorrowerBenefitValue'] = parseFloat(vals[1]) || 0;
          }
        }
      } else if (name === 'Tranche') {
        // Convert comma-separated string to array for multi-select dropdown
        const fieldId = labelToId[name] || 'TRANCHE';
        const arrayValue = typeof value === 'string' 
          ? value.split(',').map(s => s.trim()).filter(Boolean)
          : value;
        transformed[fieldId] = arrayValue;
      } else if (name === 'CUSIP' || name.includes('CUSIP')) {
        // Convert comma-separated CUSIP string to array for multi-select dropdown
        const fieldId = labelToId[name] || 'CUSIP';
        const arrayValue = typeof value === 'string' 
          ? value.split(',').map(s => s.trim()).filter(Boolean)
          : value;
        transformed[fieldId] = arrayValue;
      } else {
        // Map field name to form field ID if needed
        const fieldId = labelToId[name] || name;
        transformed[fieldId] = value;
      }
    });
    
    return transformed;
  }, [labelToId]);

  const closeFormTransform = useCallback((formData) => {
    const results = {};
    const customFields = { 'Load': false, 'Edit': 'Edit', 'Clone': 'Clone' };

    // Initialize variables to collect split field values
    let optionKey = '';
    let optionValue = '';
    const ramps = { 'CPR': '', 'CDR': '', 'Draw Rate': '' };
    const customRamps = { 'CPRRamp': '', 'CDRRamp': '', 'DrawRateRamp': '' };
    let bbAch = '';
    let bbOther = '';

    // Process form data - collect split field values first
    Object.entries(formData).forEach(([fieldId, value]) => {
      // Handle split fields according to Python logic
      if (fieldId === 'OptionRedemptionBy') {
        optionKey = value || '';
      } else if (fieldId === 'OptionRedemptionValue') {
        optionValue = value || '';
      } else if (fieldId === 'CPR' || fieldId === 'CDR' || fieldId === 'Draw Rate') {
        ramps[fieldId] = value ?? '';
      } else if (fieldId === 'CPRRamp' || fieldId === 'CDRRamp' || fieldId === 'DrawRateRamp') {
        customRamps[fieldId] = value || '';
      } else if (fieldId === 'BorrowerBenefitACH') {
        bbAch = value || '';
      } else if (fieldId === 'BorrowerBenefitOther') {
        bbOther = value || '';
      }
      // Handle Tranche field
      else if (fieldId === 'TRANCHE') {
        results['Tranche'] = Array.isArray(value) ? value.join(',') : value;
      }
      // Handle CUSIP field
      else if (fieldId === 'CUSIP') {
        const field = fields.find(f => f.id === fieldId);
        const fieldName = field?.label || 'CUSIP';
        results[fieldName] = Array.isArray(value) ? value.join(',') : value;
      }
      // All other fields
      else {
        const field = fields.find(f => f.id === fieldId);
        const fieldName = field?.label || fieldId;
        results[fieldName] = value;
      }
    });

    // Merge Option Redemption fields (matches Python logic exactly)
    if (optionValue !== '' && optionValue != null) {
      optionKey += `+${optionValue}`;
    }
    results['Option Redemption Value'] = optionKey;

    // Merge Borrower Benefit fields (matches Python logic exactly)
    const bb = `${bbAch || ''}/${bbOther || ''}`.replace(/^\/|\/$/g, '');
    results['Borrower Benefit ACH'] = bb !== '' ? bb : null;

    // Merge Ramp fields (matches Python logic exactly)
    Object.entries(ramps).forEach(([k, v]) => {
      const cr = customRamps[`${k.replace(' ', '')}Ramp`];
      let val = v ?? '';
      if (cr !== '' && cr != null) {
        val += ` using ${cr}`;
      }
      results[k] = val;
    });

    return results;
  }, [fields]);

  // Load deals only (fast and lightweight)
  const loadDeals = useCallback(async () => {
    if (preloadedData.deals.length > 0) return;

    try {
      const dealsResponse = await fetchDeals();
      const deals = Array.isArray(dealsResponse.data) ? dealsResponse.data : [];

      setPreloadedData(prev => ({
        ...prev,
        deals,
        isPreloaded: true,
      }));
    } catch (error) {
      notifyPermissionError(showNotification, error);
    }
  }, [preloadedData.deals.length, showNotification]);

  // Legacy Glint populated Option Redemption Name from this Snowflake-backed list.
  const loadOptionRedemptionNames = useCallback(async () => {
    if (preloadedData.optionRedemptionNamesLoaded || optionRedemptionNamesLoadingRef.current) return;
    optionRedemptionNamesLoadingRef.current = true;

    try {
      const response = await fetchOptionRedemptionValues();
      const optionRedemptionNames = [...new Set(
        normalizeArrayResponse(response.data)
          .map(value => String(value || '').trim())
          .filter(Boolean)
      )];

      setPreloadedData(prev => ({
        ...prev,
        optionRedemptionNames,
        optionRedemptionNamesLoaded: true,
      }));
    } catch (error) {
      notifyPermissionError(showNotification, error);
      setPreloadedData(prev => ({ ...prev, optionRedemptionNamesLoaded: true }));
    } finally {
      optionRedemptionNamesLoadingRef.current = false;
    }
  }, [preloadedData.optionRedemptionNamesLoaded, showNotification]);

  // Apply a deal's tranche_map to the TRANCHE/CUSIP dropdown choices and
  // remember it in the session cache (without overwriting selected values).
  const applyTrancheMap = useCallback((dealName, trancheMap) => {
    if (!dealName || !trancheMap) return;

    // tranche_map = { "A1": ["CUSIP1", "CUSIP2"], "A2": ["CUSIP3"], ... }
    const availableTranches = Object.keys(trancheMap);
    const availableCusips = [...new Set(Object.values(trancheMap).flat())];

    // Store tranche-CUSIP mapping
    setPreloadedData(prev => ({
      ...prev,
      trancheCusipMap: {
        ...prev.trancheCusipMap,
        [dealName]: trancheMap
      }
    }));

    // Update dropdown choices immediately (not in transition)
    // This ensures the choices are available right away
    setFields(fs =>
      fs.map(f => {
        if (f.id === 'TRANCHE') {
          // Only update if choices actually changed
          if (JSON.stringify(f.choices) === JSON.stringify(availableTranches)) {
            return f;
          }
          return { ...f, choices: availableTranches };
        }
        if (f.id === 'CUSIP') {
          // Only update if choices actually changed
          if (JSON.stringify(f.choices) === JSON.stringify(availableCusips)) {
            return f;
          }
          return { ...f, choices: availableCusips };
        }
        return f;
      })
    );
  }, []);

  // Populate dropdown choices from deal data, reusing the session cache —
  // /deal costs a full INTEX deal load server-side, so only fetch a deal's
  // tranche universe once per session.
  const populateDropdownChoicesFromDeal = useCallback(async (dealName) => {
    if (!dealName) return;

    const cached = trancheCusipMapRef.current[dealName];
    if (cached) {
      applyTrancheMap(dealName, cached);
      return;
    }

    try {
      const response = await loadDeal(dealName, null, intexDeal || null, modifiedDate);
      applyTrancheMap(dealName, response.data?.tranche_map);
    } catch (error) {
      if (!notifyPermissionError(showNotification, error)) {
        console.error('Failed to load deal choices:', error);
      }
    }
  }, [intexDeal, modifiedDate, applyTrancheMap, showNotification]);

  const loadFields = useCallback(async () => {
    if (fieldsLoaded) return; // Don't reload if already loaded
    
    setLoading(true);
    try {
      const { data } = await fetchSecurityFormFields();
      
      setFields(data.fields);
      setFieldsLoaded(true);
      if (!editData) {
        const defaults = data.fields.reduce(
          (acc, f) => f.default !== undefined ? { ...acc, [f.id]: f.default } : acc,
          {}
        );
        // Default ASOFDT to today's date in creation mode
        defaults.ASOFDT = new Date();
        // Initialize split fields with empty values for new records
        defaults.OptionRedemptionBy = '';
        defaults.OptionRedemptionValue = '';
        defaults.CPRRampType = '';
        defaults.CPRRampValue = '';
        defaults.CDRRampType = '';
        defaults.CDRRampValue = '';
        defaults.DrawRateRampType = '';
        defaults.DrawRateRampValue = '';
        defaults.BorrowerBenefitType = '';
        defaults.BorrowerBenefitValue = '';
        defaults.BorrowerBenefitACH = '';
        defaults.BorrowerBenefitOther = '';
        setFormData(defaults);
      }
    } catch (error) {
      if (!notifyPermissionError(showNotification, error)) {
        showNotification('Security fields could not be loaded. Close and reopen to retry.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [fieldsLoaded, editData, showNotification]);

  // Filter CUSIPs based on selected tranches using the cached mapping
  const updateCusipChoices = useCallback((dealName, tranches, isUserModification = false) => {
    if (!dealName || !tranches || (Array.isArray(tranches) && tranches.length === 0)) {
      // Don't clear CUSIPs if we're in edit mode and have existing CUSIPs
      setFormData(currentFormData => {
        // If we have initial edit CUSIPs and user hasn't modified tranches, preserve them
        if (editData && !isUserModification && initialEditCusipsRef.current) {
          return { ...currentFormData, CUSIP: initialEditCusipsRef.current };
        }
        // If we have existing CUSIPs (from edit data), preserve them
        if (editData && currentFormData.CUSIP && 
            (Array.isArray(currentFormData.CUSIP) ? currentFormData.CUSIP.length > 0 : currentFormData.CUSIP)) {
          return currentFormData; // Keep existing CUSIPs in edit mode
        }
        // Only update if CUSIPs are not already empty
        if (currentFormData.CUSIP && currentFormData.CUSIP.length > 0) {
          return { ...currentFormData, CUSIP: [] };
        }
        return currentFormData;
      });
      return;
    }
    
    // In edit mode, if user hasn't manually changed tranches, preserve original CUSIPs
    if (editData && !isUserModification && initialEditCusipsRef.current && !userModifiedTrancheRef.current) {
      console.log('Preserving initial edit CUSIPs (user has not modified tranches):', initialEditCusipsRef.current);
      setFormData(currentFormData => ({
        ...currentFormData,
        CUSIP: initialEditCusipsRef.current
      }));
      return;
    }
    
    const tranchesArray = Array.isArray(tranches) ? tranches : [tranches];
    
    // Use the cached tranche-CUSIP mapping to filter CUSIPs locally (no API call!)
    const dealMapping = trancheCusipMapRef.current[dealName] || {};
    let filteredCusips = [];
    
    if (Object.keys(dealMapping).length > 0) {
      // We have a mapping - filter CUSIPs based on selected tranches
      console.log('Filtering CUSIPs for tranches:', tranchesArray, 'from dealMapping:', dealMapping);
      tranchesArray.forEach(tranche => {
        const trancheCusips = dealMapping[tranche] || [];
        console.log(`Tranche ${tranche} has CUSIPs:`, trancheCusips);
        filteredCusips = [...filteredCusips, ...trancheCusips];
      });
      
      // Remove duplicates
      filteredCusips = [...new Set(filteredCusips)];
      console.log('Filtered CUSIPs result:', filteredCusips);
    } else if (editData && initialEditCusipsRef.current) {
      // In edit mode, if tranche_map hasn't loaded yet, preserve initial CUSIPs
      filteredCusips = initialEditCusipsRef.current;
    } else if (editData) {
      // In edit mode, if tranche_map hasn't loaded yet, preserve existing CUSIPs
      return;
    }
    
    // Only update if CUSIPs actually changed
    setFormData(currentFormData => {
      const currentCusips = currentFormData.CUSIP || [];
      if (JSON.stringify(currentCusips.sort()) === JSON.stringify(filteredCusips.sort())) {
        return currentFormData; // No change
      }
      return {
        ...currentFormData,
        CUSIP: filteredCusips
      };
    });
  }, [editData]);

  // Memoize deal and tranche values to prevent unnecessary updates
  const currentDeal = formData.DEAL_NAME;
  const currentTranche = formData.TRANCHE;

  // Update CUSIP choices when tranches change (filter from cached mapping)
  // Debounced to avoid excessive updates when rapidly selecting/deselecting tranches
  useEffect(() => {
    if (!open) return;
    
    // Debounce the CUSIP update by 150ms for better performance
    const timeoutId = setTimeout(() => {
      startTransition(() => {
        updateCusipChoices(currentDeal, currentTranche);
      });
    }, 150);
    
    return () => clearTimeout(timeoutId);
  }, [currentDeal, currentTranche, open, updateCusipChoices]);

  // Load blotter when CUSIPs are available (debounced)
  const currentCusips = formData.CUSIP;
  
  useEffect(() => {
    let cancelled = false;
    let timeoutId;
    
    const loadBlotterData = async () => {
      if (currentCusips && Array.isArray(currentCusips) && currentCusips.length > 0) {
        setBlotterLoading(true);
        try {
          const res = await fetchBlotter(currentCusips, modifiedDate);
          if (!cancelled) {
            const dataArray = Array.isArray(res.data) ? res.data : [];
            setBlotterData(dataArray);
            setBlotterLoading(false);
          }
        } catch (err) {
          const handledPermissionError = !cancelled && notifyPermissionError(showNotification, err);
          if (!handledPermissionError) {
            console.error('Blotter load error:', err);
          }
          if (!cancelled) {
            setBlotterData([]);
            setBlotterLoading(false);
          }
        }
      } else {
        setBlotterData([]);
        setBlotterLoading(false);
      }
    };

    // Debounce blotter loading by 600ms to avoid rapid API calls
    timeoutId = setTimeout(loadBlotterData, 600);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCusips, modifiedDate]);

  // init or reset - OPTIMIZED to cache fields
  useEffect(() => {
    if (open) {
      // Load fields and deals when modal opens
      if (!fieldsLoaded) {
        loadFields();
      } else if (!editData) {
        // Fields already loaded, set ASOFDT default for new security
        setFormData({ ASOFDT: new Date() });
      }
      // Load deals and option-redemption choices once.
      loadDeals();
      if (!embedded && !comparisonMode) {
        loadOptionRedemptionNames();
      }
    } else {
      // Reset form state when modal closes - but KEEP fields cached for performance
      setFormData({});
      setErrors({});
      setBlotterData([]);
      setFiles({ cdi: null, cashflow: null, ramp: null });
      setValidationError('');
      setDealPassword('');
      setIntexDeal('');
      // DON'T reset fields or fieldsLoaded - keep them cached for instant reopening
      // Keep cached data (deals, tranches, CUSIPs) for better performance on reopening
      processedEditDataRef.current = null; // Reset processed editData tracker
      initialEditCusipsRef.current = null; // Reset initial edit CUSIPs tracker
      userModifiedTrancheRef.current = false; // Reset user modification tracker
      loadedEditFormDataRef.current = null;
    }
  }, [
    open,
    loadFields,
    loadDeals,
    loadOptionRedemptionNames,
    fieldsLoaded,
    editData,
    embedded,
    comparisonMode,
  ]);

  // Handle editData separately - set form data from row
  useEffect(() => {
    if (!open || !editData || fields.length === 0) return;
    
    // Only process if we haven't already processed this exact editData 
    const editDataKey = JSON.stringify(editData);
    if (processedEditDataRef.current === editDataKey) {
      return;
    }
    
    processedEditDataRef.current = editDataKey; // Mark this editData as processed
    
    // Extract intexDeal from editData if available
    if (editData.intexDeal || editData.INTEX_DEAL || editData['Intex Deal']) {
      setIntexDeal(editData.intexDeal || editData.INTEX_DEAL || editData['Intex Deal'] || '');
    }
    
    // Apply field transformations
    const transformed = openFormTransform(editData);
    
    // Store initial CUSIPs from editData to preserve them
    if (transformed.CUSIP) {
      initialEditCusipsRef.current = Array.isArray(transformed.CUSIP) 
        ? transformed.CUSIP 
        : [transformed.CUSIP];
      console.log('Stored initial CUSIPs from editData:', initialEditCusipsRef.current);
    }
    
    // Reset user modification tracking for new edit session
    userModifiedTrancheRef.current = false;
    
    // Handle basic field mapping for any remaining fields
    const mapped = Object.entries(editData).reduce((acc, [label, v]) => {
      const id = labelToId[label] || label;
      if (!id) return acc;
      
      // Skip if already handled by transformation
      if (transformed.hasOwnProperty(id) || transformed.hasOwnProperty(label)) {
        return acc;
      }
      
      // Skip special fields that should be handled by transformation
      const skipFields = [
        'Tranche', 'CUSIP', 'Option Redemption Value', 'OPTIONREDEMPTVAL', 
        'Borrower Benefit ACH', 'BORROWERBENEFIT', 'RAMPS'
      ];
      if (skipFields.includes(label) || label.includes('CUSIP')) {
        return acc;
      }
      
      acc[id] = v;
      return acc;
    }, {});
    
    loadedEditFormDataRef.current = { ...mapped, ...transformed };

    // Combine transformed and mapped data; default ASOFDT to today on edit/clone
    // so the user re-runs cashflow against current data unless they explicitly change it.
    const finalData = { ...mapped, ...transformed, ASOFDT: new Date() };
    
    // Show the form immediately with the row's values — the dropdown choices
    // come from /deal (a full INTEX deal load server-side) and stream in in
    // the background instead of blocking the whole modal behind a spinner.
    setFormData(currentFormData => {
      const hasChanges = Object.keys(finalData).some(key =>
        JSON.stringify(currentFormData[key]) !== JSON.stringify(finalData[key])
      );
      const result = hasChanges ? finalData : currentFormData;
      // Capture base state for embedded diff display
      if (embedded) {
        baseFormDataRef.current = { ...result };
      }
      return result;
    });

    if (finalData.DEAL_NAME) {
      setChoicesLoading(true);
      populateDropdownChoicesFromDeal(finalData.DEAL_NAME)
        .finally(() => setChoicesLoading(false));
    }
  }, [open, editData, fields.length, labelToId, openFormTransform, populateDropdownChoicesFromDeal, embedded]);


  const handleChange = useCallback((id, value) => {
    // Batch state updates to reduce re-renders (React 18 auto-batches in event handlers)
    if (id === 'DEAL_NAME') {
      // Deal name changed - clear dependent fields
      setFormData(d => ({ ...d, [id]: value, TRANCHE: [], CUSIP: [] }));
      setErrors(e => ({ ...e, [id]: null }));
      setValidationError('');
      // Clear initial edit CUSIPs when deal changes
      initialEditCusipsRef.current = null;
      userModifiedTrancheRef.current = false;
      // Use transition for non-critical field choices update
      startTransition(() => {
        setFields(fs =>
          fs.map(f => 
            f.id === 'TRANCHE' ? { ...f, choices: [] } :
            f.id === 'CUSIP' ? { ...f, choices: [] } : f
          )
        );
      });
    } else if (id === 'TRANCHE') {
      // Tranche changed - mark that user manually modified tranches
      userModifiedTrancheRef.current = true;
      const trancheValue = Array.isArray(value) ? value : (value ? [value] : []);
      setFormData(d => ({ ...d, [id]: trancheValue }));
      setErrors(e => ({ ...e, [id]: null }));
      setValidationError('');
      
      // Immediately update CUSIPs based on new tranche selection (user modification)
      if (formData.DEAL_NAME) {
        updateCusipChoices(formData.DEAL_NAME, trancheValue, true);
      }
    } else {
      // Regular field update
      setFormData(d => ({ ...d, [id]: value }));
      setErrors(e => ({ ...e, [id]: null }));
      setValidationError('');
    }
  }, [startTransition, formData.DEAL_NAME, updateCusipChoices]);

  const handleRunToCallToggle = useCallback(checked => {
    setFormData(d => (checked ? { ...d, ...RUN_TO_CALL_VALUES } : { ...d, OptionRedemptionBy: '' }));
    setErrors(e => ({ ...e, OPTIONREDEMPTNAME: null, OptionRedemptionBy: null, OptionRedemptionValue: null }));
    setValidationError('');
  }, []);

  const [loadingCdi, setLoadingCdi] = useState(false);
  const [loadingCashflow, setLoadingCashflow] = useState(false);
  const [loadingRamp, setLoadingRamp] = useState(false);

  const processFile = useCallback(async type => {
    const file = files[type];
    if (!file) return;

    const setLoading = {
      cdi: setLoadingCdi,
      cashflow: setLoadingCashflow,
      ramp: setLoadingRamp
    }[type];

    setLoading(true);
    try {
      let res;
      if (type === 'cdi') res = await uploadCdi(file, userId, formData.DEAL_NAME || '');
      if (type === 'cashflow') res = await uploadCashflow(file);
      if (type === 'ramp') {
        res = await uploadRamp(file, userId);
        await fetchCustomRamps();
      }
      if (res?.data?.dealName) handleChange('DEAL_NAME', res.data.dealName);
      
      showNotification(`${type.toUpperCase()} uploaded successfully`, 'success');

      // Clear the file after success
      setFiles(f => ({ ...f, [type]: null }));
    } catch (err) {
      if (!notifyPermissionError(showNotification, err)) {
        showNotification(`Upload ${type} failed: ${err.message}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [files, userId, formData.DEAL_NAME, handleChange]);

  const handleLoadDeal = useCallback(async () => {
    const dealName = formData.DEAL_NAME;
    if (!dealName) {
      alert('Please select a deal name first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await loadDeal(dealName, dealPassword || null, intexDeal || null, modifiedDate);
      const dealData = response.data;
      
      // Transform and populate the form with the loaded deal data
      if (dealData) {
        const transformed = openFormTransform(dealData);
        
        // Handle type field
        if (dealData.type) {
          transformed.TYPE = dealData.type;
        }
        
        // Handle security_owner field
        if (dealData.security_owner) {
          transformed.SECURITY_OWNER = dealData.security_owner;
        }

        // Populate dropdown choices from the response's tranche_map directly —
        // calling populateDropdownChoicesFromDeal here would issue a second
        // /deal request (a full INTEX deal load) for data we already have.
        applyTrancheMap(dealName, dealData.tranche_map);

        // Then update form data with all transformed fields
        setFormData(currentFormData => ({
          ...currentFormData,
          ...transformed
        }));
       }
     } catch (err) {
       if (!notifyPermissionError(showNotification, err)) {
         alert(`Failed to load deal: ${err.message}`);
       }
     } finally {
       setLoading(false);
     }
   }, [formData.DEAL_NAME, dealPassword, modifiedDate, openFormTransform, applyTrancheMap, intexDeal, showNotification]);

  const submit = useCallback(async () => {
    setLoading(true);
    try {
      if (!String(formData.TYPE || '').trim()) {
        const message = 'Asset type is required.';
        setErrors(previous => ({ ...previous, TYPE: message }));
        showValidationFailure(`TYPE: ${message}`);
        setLoading(false);
        return;
      }

      // First, merge split fields back into combined fields (matches Python logic)
      const mergedData = closeFormTransform(formData);
      const loadedTrancheMap = preloadedData.trancheCusipMap[formData.DEAL_NAME];
      if (loadedTrancheMap) {
        const selected = Array.isArray(formData.TRANCHE) ? formData.TRANCHE : [formData.TRANCHE];
        mergedData.tranche_map = Object.fromEntries(
          selected
            .filter(Boolean)
            .map(tranche => [tranche, loadedTrancheMap[tranche] || []])
        );
      }

      // In comparison mode, skip validation and include pricing targets
      if (comparisonMode) {
        // Build pricing targets
        const pricingTargets = {};
        const hasVal = (v) => v !== '' && v !== null && v !== undefined;
        (externalTranches || []).forEach(tranche => {
          if (hasVal(cmpPrices[tranche])) {
            pricingTargets[tranche] = { price: cmpPrices[tranche] };
          } else if (hasVal(cmpYields[tranche])) {
            pricingTargets[tranche] = { yield: cmpYields[tranche] };
          } else if (hasVal(cmpSpreads[tranche])) {
            pricingTargets[tranche] = { spread: cmpSpreads[tranche] };
          } else {
            pricingTargets[tranche] = {};
          }
        });
        mergedData._pricingTargets = pricingTargets;
        if (cmpShockBps && cmpShockBps > 0) {
          mergedData._shockBps = cmpShockBps;
        }
        onSuccess?.(mergedData);
        setLoading(false);
        setCmpPrices({});
        setCmpYields({});
        setCmpSpreads({});
        setCmpShockBps('');
        onClose();
        return;
      }

      // Validate the merged data (not the split form data)
      try {
        const validationResponse = await validateSecurityForm(
          withoutAdcoGeneratedVectors(mergedData)
        );
        const valid = validationResponse.data;

        if (!valid || !valid.valid) {
          const { byId, summary } = mapValidationErrors(valid, fields);
          setErrors(byId);
          showValidationFailure(summary);
          setLoading(false);
          return;
        }

        // Clear validation error on successful validation
        setValidationError('');
      } catch (validationError) {
        if (!notifyPermissionError(showNotification, validationError)) {
          const detail = validationError?.response?.data?.error || validationError?.message || 'Unknown error';
          showValidationFailure(`Validation request failed: ${detail}`);
        }
        setLoading(false);
        return;
      }

      const finalSubmissionData = { ...mergedData };
      // A field left as loaded goes back exactly as the row had it. The form
      // re-serialises some on the way through (CPR "6.0" -> 6, a vector or ramp
      // to its first number, "Never" -> "NEVER"), which would read as an
      // assumption change and drop the stored value.
      if (editData && loadedEditFormDataRef.current) {
        const loaded = closeFormTransform(loadedEditFormDataRef.current);
        Object.keys(finalSubmissionData).forEach(key => {
          if (JSON.stringify(finalSubmissionData[key]) !== JSON.stringify(loaded[key])) return;
          if (key in editData) {
            finalSubmissionData[key] = editData[key];
          } else {
            delete finalSubmissionData[key];
          }
        });
      }

      onSuccess?.(finalSubmissionData);

      // Set loading to false before closing
      setLoading(false);

      // Close modal after successful update
      try {
        onClose();
      } catch (closeError) {
        // Force close by trying again after a delay
        setTimeout(() => {
          try {
            onClose();
          } catch (retryError) {
            // Silently fail
          }
        }, 500);
      }
    } catch (err) {
      if (!notifyPermissionError(showNotification, err)) {
        const detail = err?.response?.data?.error || err?.message || 'Unknown error';
        showValidationFailure(`Create Security failed: ${detail}`);
      }
      setLoading(false); // Only set loading to false here if there's an error
    }
  }, [formData, fields, closeFormTransform, editData, onSuccess, onClose, comparisonMode, externalTranches, cmpPrices, cmpYields, cmpSpreads, cmpShockBps, preloadedData.trancheCusipMap, showNotification, showValidationFailure]);

  // Memoized TRANCHE field component for better performance
  const TrancheField = useMemo(() => {
    const f = fields.find(field => field.id === 'TRANCHE');
    if (!f) return null;
    
    const val = formData[f.id] ?? [];
    const err = errors[f.id];
    const isFieldDisabled = loading;
    
    return (
      <Autocomplete
        key={f.id}
        options={f.choices || []}
        value={Array.isArray(val) ? val : (val ? [val] : [])}
        onChange={(e, v) => handleChange(f.id, v || [])}
        multiple
        disableCloseOnSelect
        disabled={isFieldDisabled || !formData.DEAL_NAME}
        loading={choicesLoading}
        renderInput={params => (
          <TextField
            {...params}
            label={f.label}
            fullWidth
            size="medium"
            disabled={isFieldDisabled || !formData.DEAL_NAME}
            error={!!err}
            helperText={
              !formData.DEAL_NAME ? 'Select a deal first' :
              choicesLoading ? 'Loading tranches from INTEX…' : err
            }
            sx={fieldStyles}
            InputProps={{
              ...params.InputProps,
              endAdornment: params.InputProps.endAdornment,
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option}
              label={option}
              size="small"
              sx={{
                bgcolor: '#e0f2fe',
                color: '#0277bd',
                fontWeight: 600,
                fontSize: '11px',
                height: 24,
                '& .MuiChip-deleteIcon': {
                  color: '#0277bd',
                  fontSize: '16px',
                  '&:hover': {
                    color: '#01579b'
                  }
                }
              }}
            />
          ))
        }
        sx={{
          '& .MuiAutocomplete-inputRoot': {
            padding: '8px 12px',
            gap: 1
          }
        }}
      />
    );
  }, [fields, formData.TRANCHE, formData.DEAL_NAME, errors.TRANCHE, loading, choicesLoading, fieldStyles, handleChange]);

  const renderField = useCallback(f => {
    const val = formData[f.id] ?? '';
    const err = errors[f.id];
    const isFieldDisabled = loading;
    
    // Use memoized field styles instead of recreating on every render
    const common = { 
      fullWidth: true, 
      size: 'medium', 
      required: f.id === 'TYPE',
      disabled: isFieldDisabled, 
      error: !!err, 
      helperText: err,
      sx: fieldStyles
    };

    if (isAdcoGeneratedVector(f.id, val, formData)) {
      const canOverrideAdco = !embedded && !comparisonMode;
      return (
        <Tooltip
          key={f.id}
          title={val}
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 640,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                fontFamily: 'monospace',
                fontSize: 11,
              },
            },
          }}
        >
          <Box>
            <TextField
              label={f.label}
              value="ADCO Generated"
              InputProps={{
                readOnly: true,
                endAdornment: canOverrideAdco ? (
                  <Button
                    size="small"
                    aria-label={`Clear ${f.label} ADCO value for static override`}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => handleChange(f.id, '')}
                    sx={{
                      minWidth: 'auto',
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                    }}
                  >
                    Clear / override
                  </Button>
                ) : undefined,
              }}
              {...common}
              helperText={err || (
                canOverrideAdco
                  ? 'Generated by ADCO · clear to enter a static value'
                  : 'Generated by ADCO · hover to view vector'
              )}
              sx={{
                ...fieldStyles,
                '& .MuiInputBase-input': {
                  color: 'info.main',
                  fontWeight: 700,
                  textDecorationLine: 'underline',
                  textDecorationStyle: 'dotted',
                  textUnderlineOffset: '3px',
                  cursor: 'help',
                },
              }}
            />
          </Box>
        </Tooltip>
      );
    }
    
    switch (f.datatype) {
      case 'string':
        // Special handling for TRANCHE field - use memoized component
        if (f.id === 'TRANCHE') {
          return TrancheField;
        }

        if (f.id === 'OPTIONREDEMPTNAME' && !embedded && !comparisonMode) {
          return (
            <Autocomplete
              key={f.id}
              freeSolo
              options={preloadedData.optionRedemptionNames}
              value={val || null}
              inputValue={String(val || '')}
              onChange={(event, value) => handleChange(f.id, value || '')}
              onInputChange={(event, value, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  handleChange(f.id, value);
                }
              }}
              loading={!preloadedData.optionRedemptionNamesLoaded}
              disabled={isFieldDisabled}
              renderInput={params => (
                <TextField
                  {...params}
                  label={f.label}
                  {...common}
                  helperText={
                    err
                    || (!preloadedData.optionRedemptionNamesLoaded
                      ? 'Loading option redemption names…'
                      : undefined)
                  }
                />
              )}
            />
          );
        }
        
                 // Special handling for CUSIP field - always render as multi-select read-only dropdown
         if (f.id === 'CUSIP') {
           return (
             <Autocomplete
               key={f.id}
               options={f.choices || []}
               value={Array.isArray(val) ? val : (val ? [val] : [])}
               multiple
               disabled={true}
               readOnly
               renderInput={params => (
                 <TextField 
                   {...params} 
                   label={f.label} 
                   {...common}
                   helperText={editData ? 'Read-only: Existing CUSIPs' :
                              !formData.DEAL_NAME ? 'Select a deal first' : 
                              !formData.TRANCHE || (Array.isArray(formData.TRANCHE) ? formData.TRANCHE.length === 0 : !formData.TRANCHE) ? 'Select tranches to see CUSIPs' : 
                              'CUSIPs from selected tranches'}
                   InputProps={{
                     ...params.InputProps,
                     endAdornment: params.InputProps.endAdornment,
                   }}
                   sx={{
                     '& .MuiInputBase-root': {
                       minHeight: '56px', // Make it taller to accommodate multiple chips
                     },
                     '& .MuiAutocomplete-tag': {
                       maxWidth: '200px', // Limit individual chip width
                     }
                   }}
                 />
               )}
               renderTags={(tagValue, getTagProps) =>
                 tagValue.map((option, index) => (
                   <Chip
                     {...getTagProps({ index })}
                     key={option}
                     label={option}
                     size="small"
                     sx={theme => ({
                       bgcolor: theme.palette.mode === 'dark'
                         ? alpha(theme.palette.primary.light, 0.25)
                         : '#e0f2fe',
                       color: theme.palette.mode === 'dark' ? '#93c5fd' : '#0277bd',
                       fontWeight: 600,
                       // field is read-only by design; don't let the disabled state dim the chips
                       '&.Mui-disabled': { opacity: 1 },
                     })}
                   />
                 ))
               }
             />
           );
         }
        
        // Security owner: suggest known owners but accept a new one
        if (f.id === 'SEC_OWNER' || f.id === 'SECURITY_OWNER') {
          return (
            <Autocomplete
              key={f.id}
              freeSolo
              options={f.choices || []}
              value={val || null}
              inputValue={String(val || '')}
              onChange={(event, value) => handleChange(f.id, value || '')}
              onInputChange={(event, value, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  handleChange(f.id, value);
                }
              }}
              disabled={isFieldDisabled}
              renderInput={params => <TextField {...params} label={f.label} {...common} />}
            />
          );
        }

        // Other string fields with choices
        if (f.choices?.length) {
          return (
            <Autocomplete
              key={f.id}
              options={f.choices}
              value={val || null}
              onChange={(e, v) => handleChange(f.id, v || '')}
              renderInput={params => <TextField {...params} label={f.label} {...common} />}
            />
          );
        }
        return <TextField key={f.id} label={f.label} value={val} onChange={e => handleChange(f.id, e.target.value)} {...common} />;
      case 'number':
        return <TextField key={f.id} label={f.label} type="number" value={val} onChange={e => handleChange(f.id, +e.target.value || 0)} {...common} />;
      case 'date':
        return (
          <LocalizationProvider key={f.id} dateAdapter={AdapterDateFns}>
            <DatePicker label={f.label} format="MM/dd/yyyy" value={dateOnlyToPickerValue(val)} onChange={d => handleChange(f.id, pickerValueToDateOnly(d))} renderInput={params => <TextField {...params} {...common} />} />
          </LocalizationProvider>
        );
      case 'boolean':
        return <FormControlLabel key={f.id} control={<Switch checked={val || false} onChange={e => handleChange(f.id, e.target.checked)} disabled={isFieldDisabled} />} label={f.label} />;
      default:
        return <TextField key={f.id} label={f.label} value={val} onChange={e => handleChange(f.id, e.target.value)} {...common} />;
    }
  }, [
    formData,
    errors,
    loading,
    fieldStyles,
    handleChange,
    embedded,
    comparisonMode,
    preloadedData.optionRedemptionNames,
    preloadedData.optionRedemptionNamesLoaded,
    TrancheField,
    editData,
  ]);

  const uploadSection = useCallback(type => (
    <Paper 
      key={type} 
      variant="outlined" 
      sx={{ 
        p: 2, 
        border: '2px dashed', 
        borderColor: '#d1d5db', 
        borderRadius: 2,
        height: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
          borderColor: '#0284c7', 
          bgcolor: 'rgba(2, 132, 199, 0.05)'
        },
        transition: 'all 0.2s ease'
      }}
    >
      <Box 
        sx={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}
      >
        <input id={type} hidden type="file" onChange={e => setFiles(f => ({ ...f, [type]: e.target.files[0] }))} />
        <label htmlFor={type} style={{ width: isMobile ? '100%' : 'auto' }}>
          <Button 
            component="span" 
            startIcon={<CloudUploadIcon />} 
            disabled={loading || {cdi: loadingCdi, cashflow: loadingCashflow, ramp: loadingRamp}[type]}
            variant="outlined"
            fullWidth={isMobile}
            sx={{
              borderColor: '#0284c7',
              color: '#0284c7',
              fontWeight: 600,
              textTransform: 'none',
              px: 2,
              py: 0.75,
              fontSize: '12px',
              whiteSpace: 'nowrap',
              '&:hover': {
                bgcolor: 'rgba(2, 132, 199, 0.1)',
                borderColor: '#0369a1'
              }
            }}
          >
            Upload {type.toUpperCase()}
          </Button>
        </label>
        {files[type] && (
          <>
            <Chip 
              label={files[type].name} 
              onDelete={() => setFiles(f => ({ ...f, [type]: null }))}
              size="small"
              sx={{
                maxWidth: '100%',
                fontSize: '11px',
                '& .MuiChip-label': {
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  px: 1.5
                }
              }}
            />
            <Button 
              size="small" 
              onClick={() => processFile(type)} 
              disabled={loading || {cdi: loadingCdi, cashflow: loadingCashflow, ramp: loadingRamp}[type]}
              fullWidth={isMobile}
              variant="contained"
              sx={{
                whiteSpace: 'nowrap',
                fontSize: '11px',
                py: 0.5,
                px: 1.5
              }}
            >
              { {cdi: loadingCdi, cashflow: loadingCashflow, ramp: loadingRamp}[type] ? <CircularProgress size={14} /> : 'Process' }
            </Button>
          </>
        )}
      </Box>
    </Paper>
  ), [files, loading, processFile, isMobile, loadingCdi, loadingCashflow, loadingRamp]);

  // Memoize field ordering and filtering to prevent recomputation on every render
  const orderedFields = useMemo(() => {
    const filteredFields = fields.filter(f=>!['MODIFIED_BY','MODIFIED_DATE','CLONE','EDIT','LOAD','STATUS','INTEX_DEAL','OWN_PCT'].includes(f.id));
    const priorityOrder = ['ASOFDT', 'DEAL_NAME', 'TYPE', 'TRANCHE', 'CUSIP'];
    const priorityFields = priorityOrder.map(id => filteredFields.find(f => f.id === id)).filter(Boolean);
    const remainingFields = filteredFields.filter(f => !priorityOrder.includes(f.id));
    return [...priorityFields, ...remainingFields];
  }, [fields]);

  // Helper to determine field size based on type and importance - memoized
  const getFieldSize = useCallback((field) => {
    if (['DESCRIPTION', 'NOTES'].includes(field.id)) {
      return { size: { xs: 12 } };
    }
    if (['OptionRedemptionBy', 'OptionRedemptionValue', 'CPRRampType', 'CPRRampValue', 'CDRRampType', 'CDRRampValue', 'DrawRateRampType', 'DrawRateRampValue', 'BorrowerBenefitType', 'BorrowerBenefitValue', 'BorrowerBenefitACH', 'BorrowerBenefitOther'].includes(field.id)) {
      return { size: { xs: 12, sm: 6, lg: 4 } };
    }
    if (['CUSIP', 'DEAL_NAME', 'TRANCHE', 'ASOFDT', 'FACE_VALUE', 'PRICE', 'YIELD'].includes(field.id)) {
      return { size: { xs: 12, md: 6 } };
    }
    return { size: { xs: 12, sm: 6, lg: 4 } };
  }, []);

  // Don't render heavy content until modal is actually open
  if (!open && !fieldsLoaded) {
    return null;
  }

  // --- Embedded mode: render form content directly without Dialog wrapper ---
  if (embedded) {
    return (
      <Box sx={{ bgcolor: theme.palette.background.paper }}>
        {/* Validation Error Bar */}
        <Collapse in={!!validationError} ref={validationBannerRef}>
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-message': { fontSize: '14px', fontWeight: 500 } }}
            onClose={() => setValidationError('')}
          >
            {validationError}
          </Alert>
        </Collapse>

        <Box>
          {/* Security Details Accordion */}
          <Accordion
            defaultExpanded
            sx={{
              mb: 3, borderRadius: 2,
              boxShadow: theme.palette.mode === 'dark' ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              border: `1px solid ${theme.palette.divider}`,
              '&:before': { display: 'none' },
              bgcolor: theme.palette.background.paper,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.5) : '#f8fafc',
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px 8px 0 0',
                '&.Mui-expanded': { bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.7) : '#f1f5f9', minHeight: 56 },
                px: 3, py: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                    Security Details
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '12px' }}>
                    Modify parameters for scenario comparison
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 4, bgcolor: theme.palette.background.paper }}>
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '200px', py: 4 }}>
                  <CircularProgress size={40} sx={{ color: '#0284c7', mb: 2 }} />
                  <Typography sx={{ color: theme.palette.text.secondary, fontSize: '14px' }}>Loading security details...</Typography>
                </Box>
              ) : (
                <Grid container spacing={2.5}>
                  {(() => {
                    const base = baseFormDataRef.current || {};
                    const renderWithDiff = (field) => {
                      const currentVal = formData[field.id];
                      const baseVal = base[field.id];
                      const currentStr = currentVal != null && currentVal !== '' ? String(Array.isArray(currentVal) ? currentVal.join(',') : currentVal) : '';
                      const baseStr = baseVal != null && baseVal !== '' ? String(Array.isArray(baseVal) ? baseVal.join(',') : baseVal) : '';
                      const isDiff = currentStr !== baseStr && currentStr !== '';
                      return (
                        <Box>
                          {field.help ? (
                            <Tooltip title={field.help} arrow placement="top-start" enterDelay={500}>
                              <Box>{renderField(field)}</Box>
                            </Tooltip>
                          ) : renderField(field)}
                          {isDiff && (
                            <Typography sx={{ fontSize: '10px', color: '#f59e0b', mt: 0.25, fontWeight: 600 }}>
                              {baseStr || '(empty)'} &rarr; {currentStr}
                            </Typography>
                          )}
                        </Box>
                      );
                    };
                    return orderedFields.flatMap(f => {
                      if (f.id === 'OPTIONREDEMPTVAL') {
                        const redemptionByField = {
                          id: 'OptionRedemptionBy',
                          label: 'Option Redemption By',
                          datatype: 'string',
                          choices: ['REINVESTEND', 'CALL', 'NONCALL'],
                        };
                        const redemptionValueField = { id: 'OptionRedemptionValue', label: 'Option Redemption Value', datatype: 'number' };
                        return [
                          <Grid item {...getFieldSize(redemptionByField)} key="OptionRedemptionBy">{renderWithDiff(redemptionByField)}</Grid>,
                          <Grid item {...getFieldSize(redemptionValueField)} key="OptionRedemptionValue">{renderWithDiff(redemptionValueField)}</Grid>,
                        ];
                      }
                      if (f.id === 'RAMPS') {
                        const rampFields = [
                          { id: 'CPRRampType', label: 'CPR Ramp Type', datatype: 'string', choices: ['LINEAR', 'EXPONENTIAL', 'STEP', 'NONE'] },
                          { id: 'CPRRampValue', label: 'CPR Ramp Value', datatype: 'number' },
                          { id: 'CDRRampType', label: 'CDR Ramp Type', datatype: 'string', choices: ['LINEAR', 'EXPONENTIAL', 'STEP', 'NONE'] },
                          { id: 'CDRRampValue', label: 'CDR Ramp Value', datatype: 'number' },
                          { id: 'DrawRateRampType', label: 'Draw Rate Ramp Type', datatype: 'string', choices: ['LINEAR', 'EXPONENTIAL', 'STEP', 'NONE'] },
                          { id: 'DrawRateRampValue', label: 'Draw Rate Ramp Value', datatype: 'number' },
                        ];
                        return rampFields.map(rf => <Grid item {...getFieldSize(rf)} key={rf.id}>{renderWithDiff(rf)}</Grid>);
                      }
                      if (f.id === 'BORROWERBENEFIT') {
                        const bbFields = [
                          { id: 'BorrowerBenefitType', label: 'Borrower Benefit Type', datatype: 'string', choices: ['ACH', 'OTHER', 'WIRE', 'CHECK'] },
                          { id: 'BorrowerBenefitValue', label: 'Borrower Benefit Value', datatype: 'number' },
                        ];
                        return bbFields.map(bf => <Grid item {...getFieldSize(bf)} key={bf.id}>{renderWithDiff(bf)}</Grid>);
                      }
                      if (f.id === 'Borrower Benefit ACH') {
                        const legacyFields = [
                          { id: 'BorrowerBenefitACH', label: 'Borrower Benefit ACH', datatype: 'string' },
                          { id: 'BorrowerBenefitOther', label: 'Borrower Benefit Other', datatype: 'string' },
                        ];
                        return legacyFields.map(lf => <Grid item {...getFieldSize(lf)} key={lf.id}>{renderWithDiff(lf)}</Grid>);
                      }
                      return (<Grid item {...getFieldSize(f)} key={f.id}>{renderWithDiff(f)}</Grid>);
                    });
                  })()}
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Apply button for embedded mode */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={submit}
            disabled={loading}
            sx={{
              fontWeight: 600, borderRadius: 2, px: 4, py: 1.5,
              textTransform: 'none', fontSize: '14px',
            }}
          >
            {loading ? 'Applying...' : 'Apply Scenario Parameters'}
          </Button>
        </Box>

      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={fullScreen}
      keepMounted={false}
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#1e293b',
          color: 'white',
          py: 3,
          px: 4,
          borderBottom: theme.palette.mode === 'dark' ? '1px solid #334155' : '1px solid #334155'
        }}
      >
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600,
              fontSize: '20px',
              mb: 0.5,
              letterSpacing: '-0.025em'
            }}
          >
        {comparisonMode ? 'Configure Scenario' : editData ? 'Edit Security' : 'Add New Security'}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '14px'
            }}
          >
            {editData ? 'Modify existing security details and configurations' : 'Create a new financial security with all required parameters'}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: theme.palette.background.paper }}>
        {/* Validation Error Bar */}
        <Collapse in={!!validationError} ref={validationBannerRef}>
          <Alert
            severity="error"
            sx={{
              mx: 3,
              mt: 2,
              mb: 1,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '14px',
                fontWeight: 500
              }
            }}
            onClose={() => setValidationError('')}
          >
            {validationError}
          </Alert>
        </Collapse>

        <Box sx={{ p: 4 }}>
          {/* Security Details Section */}
          <Accordion 
            defaultExpanded 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              border: `1px solid ${theme.palette.divider}`,
              '&:before': { display: 'none' },
              bgcolor: theme.palette.background.paper
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />} 
              sx={{ 
                bgcolor: theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.background.default, 0.5)
                  : '#f8fafc',
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px 8px 0 0',
                '&.Mui-expanded': { 
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.7)
                    : '#f1f5f9',
                  minHeight: 56
                },
                px: 3,
                py: 1
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: isMobile ? 'stretch' : 'center', 
                justifyContent: 'space-between', 
                width: '100%', 
                pr: isMobile ? 0 : 2,
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 2 : 0
              }}>
                <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                    Security Details
                  </Typography>
                  {!isMobile && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '12px' }}>
                      Configure all security parameters and metadata
                    </Typography>
                  )}
                </Box>
                {!comparisonMode && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? 1 : 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <TextField
                    type="password"
                    // Per-deal access code, not a login: stop the browser autofilling a saved
                    // credential, which drops its "username" into the grid's Deal Name filter.
                    autoComplete="new-password"
                    placeholder="Password (optional)"
                    value={dealPassword}
                    onChange={(e) => {
                      e.stopPropagation(); // Prevent accordion toggle
                      setDealPassword(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent accordion toggle on click
                    disabled={loading}
                    size="small"
                    fullWidth={isMobile}
                    sx={{
                      width: isMobile ? '100%' : 180,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: theme.palette.background.paper,
                        fontSize: '14px',
                        height: isMobile ? 50 : 42,
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        }
                      },
                      '& .MuiInputBase-input': {
                        padding: isMobile ? '14px' : '10px 14px'
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth={isMobile}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent accordion toggle
                      handleLoadDeal();
                    }}
                    disabled={!formData.DEAL_NAME || loading}
                    sx={{
                      fontWeight: 600,
                      px: 3,
                      py: isMobile ? 2 : 1.5,
                      textTransform: 'none',
                      fontSize: '16px',
                      minHeight: isMobile ? 50 : 42,
                      borderRadius: 2
                    }}
                  >
                    Load Deal
                  </Button>
                </Box>
                )}
              </Box>
          </AccordionSummary>
            <AccordionDetails sx={{ p: 4, bgcolor: theme.palette.background.paper }}>
              {loading ? (
                <Box 
                  sx={{
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: '300px',
                    py: 6
                  }}
                >
                  <CircularProgress size={50} sx={{ color: '#0284c7', mb: 2 }} />
                  <Typography sx={{ color: theme.palette.text.secondary, fontSize: '14px' }}>
                    Loading security details...
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2.5}>
                  {orderedFields.flatMap(f => {
                   // Special handling for split fields
                   
                   // Option Redemption Value → Option Redemption By + Option Redemption Value
                   if (f.id === 'OPTIONREDEMPTVAL') {
                     const redemptionByField = {
                       id: 'OptionRedemptionBy',
                       label: 'Option Redemption By',
                       datatype: 'string',
                       choices: f.choices?.length
                         ? f.choices
                         : DEFAULT_OPTION_REDEMPTION_BY_CHOICES
                     };
                     const redemptionValueField = {
                       id: 'OptionRedemptionValue',
                       label: 'Option Redemption Value',
                       datatype: 'number'
                     };
                     
                     return [
                       <Grid item {...getFieldSize(redemptionByField)} key="OptionRedemptionBy">
                         {renderField(redemptionByField)}
                       </Grid>,
                       <Grid item {...getFieldSize(redemptionValueField)} key="OptionRedemptionValue">
                         {renderField(redemptionValueField)}
                       </Grid>,
                       <Grid item {...getFieldSize(redemptionValueField)} key="RunToCall" sx={{ display: 'flex', alignItems: 'center' }}>
                         <Tooltip title={RUN_TO_CALL_HELP} arrow placement="top">
                           <FormControlLabel
                             control={(
                               <Switch
                                 checked={isRunToCall(formData)}
                                 onChange={e => handleRunToCallToggle(e.target.checked)}
                                 disabled={loading}
                                 inputProps={{ 'aria-label': 'Run to call' }}
                               />
                             )}
                             label="Run to call"
                           />
                         </Tooltip>
                       </Grid>
                     ];
                   }
                   
                   // Ramps → CPR Ramp Type + CPR Ramp Value, CDR Ramp Type + CDR Ramp Value, Draw Rate Ramp Type + Draw Rate Ramp Value
                   if (f.id === 'RAMPS') {
                     const cprRampTypeField = {
                       id: 'CPRRampType',
                       label: 'CPR Ramp Type',
                       datatype: 'string',
                       choices: ['LINEAR', 'EXPONENTIAL', 'STEP', 'NONE']
                     };
                     const cprRampValueField = {
                       id: 'CPRRampValue',
                       label: 'CPR Ramp Value',
                       datatype: 'number'
                     };
                     const cdrRampTypeField = {
                       id: 'CDRRampType',
                       label: 'CDR Ramp Type',
                       datatype: 'string',
                       choices: ['LINEAR', 'EXPONENTIAL', 'STEP', 'NONE']
                     };
                     const cdrRampValueField = {
                       id: 'CDRRampValue',
                       label: 'CDR Ramp Value',
                       datatype: 'number'
                     };
                     const drawRateRampTypeField = {
                       id: 'DrawRateRampType',
                       label: 'Draw Rate Ramp Type',
                       datatype: 'string',
                       choices: ['LINEAR', 'EXPONENTIAL', 'STEP', 'NONE']
                     };
                     const drawRateRampValueField = {
                       id: 'DrawRateRampValue',
                       label: 'Draw Rate Ramp Value',
                       datatype: 'number'
                     };
                     
                     return [
                       <Grid item {...getFieldSize(cprRampTypeField)} key="CPRRampType">
                         {renderField(cprRampTypeField)}
                       </Grid>,
                       <Grid item {...getFieldSize(cprRampValueField)} key="CPRRampValue">
                         {renderField(cprRampValueField)}
                       </Grid>,
                       <Grid item {...getFieldSize(cdrRampTypeField)} key="CDRRampType">
                         {renderField(cdrRampTypeField)}
                       </Grid>,
                       <Grid item {...getFieldSize(cdrRampValueField)} key="CDRRampValue">
                         {renderField(cdrRampValueField)}
                       </Grid>,
                       <Grid item {...getFieldSize(drawRateRampTypeField)} key="DrawRateRampType">
                         {renderField(drawRateRampTypeField)}
                       </Grid>,
                       <Grid item {...getFieldSize(drawRateRampValueField)} key="DrawRateRampValue">
                         {renderField(drawRateRampValueField)}
                       </Grid>
                     ];
                   }
                   
                   // Borrower Benefit → Borrower Benefit Type + Borrower Benefit Value
                   if (f.id === 'BORROWERBENEFIT') {
                     const borrowerBenefitTypeField = {
                       id: 'BorrowerBenefitType',
                       label: 'Borrower Benefit Type',
                       datatype: 'string',
                       choices: ['ACH', 'OTHER', 'WIRE', 'CHECK']
                     };
                     const borrowerBenefitValueField = {
                       id: 'BorrowerBenefitValue',
                       label: 'Borrower Benefit Value',
                       datatype: 'number'
                     };

                     return [
                       <Grid item {...getFieldSize(borrowerBenefitTypeField)} key="BorrowerBenefitType">
                         {renderField(borrowerBenefitTypeField)}
                       </Grid>,
                       <Grid item {...getFieldSize(borrowerBenefitValueField)} key="BorrowerBenefitValue">
                         {renderField(borrowerBenefitValueField)}
                       </Grid>
                     ];
                   }

                   // Legacy Borrower Benefit ACH field (Python version compatibility)
                   if (f.id === 'Borrower Benefit ACH') {
                     const borrowerBenefitACHField = {
                       id: 'BorrowerBenefitACH',
                       label: 'Borrower Benefit ACH',
                       datatype: 'string'
                     };
                     const borrowerBenefitOtherField = {
                       id: 'BorrowerBenefitOther',
                       label: 'Borrower Benefit Other',
                       datatype: 'string'
                     };

                     return [
                       <Grid item {...getFieldSize(borrowerBenefitACHField)} key="BorrowerBenefitACH">
                         {renderField(borrowerBenefitACHField)}
                       </Grid>,
                       <Grid item {...getFieldSize(borrowerBenefitOtherField)} key="BorrowerBenefitOther">
                         {renderField(borrowerBenefitOtherField)}
                       </Grid>
                     ];
                   }
                   
                   // Regular field rendering
                   return (
                     <Grid item {...getFieldSize(f)} key={f.id}>
                     {f.help ? (
                       <Tooltip title={f.help} arrow placement="top-start" enterDelay={500}>
                         <Box>{renderField(f)}</Box>
                       </Tooltip>
                     ) : renderField(f)}
                   </Grid>
                   );
                 })}
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Pricing Targets - Comparison Mode Only */}
          {comparisonMode && externalTranches.length > 0 && (
            <Accordion
              defaultExpanded
              sx={{
                mb: 3, borderRadius: 2,
                boxShadow: theme.palette.mode === 'dark' ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: `1px solid ${theme.palette.divider}`,
                '&:before': { display: 'none' },
                bgcolor: theme.palette.background.paper,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.5) : '#f8fafc',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px 8px 0 0',
                  '&.Mui-expanded': { bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.7) : '#f1f5f9', minHeight: 56 },
                  px: 3, py: 1,
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                    Pricing Targets
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '12px' }}>
                    Set price, yield, or spread targets per tranche (optional, mutually exclusive)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3, bgcolor: theme.palette.background.paper }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `140px repeat(${externalTranches.length}, 1fr)`,
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  {/* Header */}
                  <Box />
                  {externalTranches.map(t => (
                    <Typography key={`h-${t}`} sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>
                      {t}
                    </Typography>
                  ))}
                  {/* Price Row */}
                  <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '13px' }}>Price</Typography>
                  {externalTranches.map(t => {
                    const dis = (cmpYields[t] !== '' && cmpYields[t] != null) || (cmpSpreads[t] !== '' && cmpSpreads[t] != null);
                    return (
                      <TextField key={`p-${t}`} fullWidth placeholder="100.00" type="number" size="small" disabled={dis}
                        value={cmpPrices[t] ?? ''} onChange={e => setCmpPrices(prev => ({ ...prev, [t]: e.target.value === '' ? '' : Number(e.target.value) }))}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '13px', bgcolor: dis ? (theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5') : theme.palette.background.paper, '& input': { textAlign: 'center', py: 0.75 } } }}
                      />
                    );
                  })}
                  {/* Yield Row */}
                  <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '13px' }}>Yield %</Typography>
                  {externalTranches.map(t => {
                    const dis = (cmpPrices[t] !== '' && cmpPrices[t] != null) || (cmpSpreads[t] !== '' && cmpSpreads[t] != null);
                    return (
                      <TextField key={`y-${t}`} fullWidth placeholder="5.00" type="number" size="small" disabled={dis}
                        value={cmpYields[t] ?? ''} onChange={e => setCmpYields(prev => ({ ...prev, [t]: e.target.value === '' ? '' : Number(e.target.value) }))}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '13px', bgcolor: dis ? (theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5') : theme.palette.background.paper, '& input': { textAlign: 'center', py: 0.75 } } }}
                      />
                    );
                  })}
                  {/* Spread Row */}
                  <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '13px' }}>Spread (bps)</Typography>
                  {externalTranches.map(t => {
                    const dis = (cmpPrices[t] !== '' && cmpPrices[t] != null) || (cmpYields[t] !== '' && cmpYields[t] != null);
                    return (
                      <TextField key={`s-${t}`} fullWidth placeholder="150" type="number" size="small" disabled={dis}
                        value={cmpSpreads[t] ?? ''} onChange={e => setCmpSpreads(prev => ({ ...prev, [t]: e.target.value === '' ? '' : Number(e.target.value) }))}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '13px', bgcolor: dis ? (theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5') : theme.palette.background.paper, '& input': { textAlign: 'center', py: 0.75 } } }}
                      />
                    );
                  })}
                  {/* Shock Row */}
                  <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '13px' }}>Shock (bps)</Typography>
                  <Box sx={{ gridColumn: `span ${externalTranches.length}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      fullWidth placeholder="e.g. 50" type="number" size="small"
                      value={cmpShockBps} onChange={e => setCmpShockBps(e.target.value === '' ? '' : Number(e.target.value))}
                      sx={{ maxWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 1, bgcolor: cmpShockBps ? alpha('#ff9800', 0.08) : theme.palette.background.paper, fontSize: '13px', '& input': { textAlign: 'center', py: 0.75 } } }}
                    />
                    {cmpShockBps > 0 && (
                      <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                        Will run +{cmpShockBps}, Base, -{cmpShockBps} scenarios
                      </Typography>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Blotter Section - Hidden on Mobile and Comparison Mode */}
          {!isMobile && !comparisonMode && (
          <Accordion 
            defaultExpanded
            sx={{ 
              mb: 3,
              borderRadius: 2,
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              border: `1px solid ${theme.palette.divider}`,
              '&:before': { display: 'none' },
              bgcolor: theme.palette.background.paper
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />} 
              sx={{ 
                bgcolor: theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.background.default, 0.5)
                  : '#f8fafc',
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px 8px 0 0',
                '&.Mui-expanded': { 
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.7)
                    : '#f1f5f9',
                  minHeight: 56
                },
                px: 3,
                py: 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                    Blotter
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '12px' }}>
                    {blotterLoading 
                      ? 'Loading blotter data...' 
                      : blotterData.length > 0 
                        ? `Showing ${blotterData.length} record${blotterData.length !== 1 ? 's' : ''}`
                        : 'Select a deal and tranches to view blotter data'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (formData.CUSIP && formData.CUSIP.length > 0) {
                      setBlotterLoading(true);
                      try {
                        const res = await fetchBlotter(formData.CUSIP, modifiedDate);
                        setBlotterData(Array.isArray(res.data) ? res.data : []);
                      } catch (err) {
                        if (!notifyPermissionError(showNotification, err)) {
                          console.error('Refresh Blotter Error:', err);
                        }
                      } finally {
                        setBlotterLoading(false);
                      }
                    } else {
                      alert('No CUSIPs selected. Please load a deal and select tranches first.');
                    }
                  }}
                  sx={{ fontWeight: 600, textTransform: 'none' }}
                >
                  Refresh Blotter
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3, bgcolor: theme.palette.background.paper }}>
              <BlotterGrid data={blotterData} loading={blotterLoading} />
            </AccordionDetails>
          </Accordion>
          )}

          {/* File Uploads Section - Hidden on Mobile and Comparison Mode */}
          {!isMobile && !comparisonMode && (
          <Accordion 
            defaultExpanded 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              border: `1px solid ${theme.palette.divider}`,
              '&:before': { display: 'none' },
              bgcolor: theme.palette.background.paper
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />} 
              sx={{ 
                bgcolor: theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.background.default, 0.5)
                  : '#f8fafc',
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px 8px 0 0',
                '&.Mui-expanded': { 
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.7)
                    : '#f1f5f9',
                  minHeight: 56
                },
                px: 3,
                py: 1
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                  File Uploads
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '12px' }}>
                  Upload CDI, cashflow, and ramp data files
                </Typography>
              </Box>
          </AccordionSummary>
            <AccordionDetails sx={{ p: 4, bgcolor: theme.palette.background.paper }}>
              <Grid container spacing={2.5}>
                {['cdi','cashflow','ramp'].map(t => (
                  <Grid item size={{ xs: 12, lg: 4 }} key={t}>
                    {uploadSection(t)}
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions 
        sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.default, 0.5)
            : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          p: 3,
          gap: 2,
          justifyContent: 'flex-end',
          flexDirection: isMobile ? 'column-reverse' : 'row'
        }}
      >
        <Button 
          onClick={onClose} 
          disabled={loading} 
          variant="outlined" 
          size="large"
          fullWidth={isMobile}
          sx={{
            borderColor: theme.palette.text.secondary,
            color: theme.palette.text.primary,
            fontWeight: 600,
            borderRadius: 2,
            px: 4,
            py: isMobile ? 2 : 1.5,
            textTransform: 'none',
            fontSize: isMobile ? '15px' : '14px',
            minWidth: isMobile ? 'auto' : 120,
            minHeight: isMobile ? 50 : 'auto',
            '&:hover': {
              borderColor: theme.palette.text.primary,
              bgcolor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.paper, 0.8)
                : alpha(theme.palette.primary.main, 0.04),
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease',
            '&:disabled': {
              borderColor: theme.palette.action.disabled,
              color: theme.palette.text.disabled
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={submit} 
          variant="contained" 
          color="primary"
          disabled={loading || !formData.TRANCHE || (Array.isArray(formData.TRANCHE) && formData.TRANCHE.length === 0)} 
          size="large"
          fullWidth={isMobile}
          sx={{ 
            fontWeight: 600,
            borderRadius: 2,
            px: 4,
            py: isMobile ? 2 : 1.5,
            textTransform: 'none',
            fontSize: isMobile ? '15px' : '14px',
            minWidth: isMobile ? 'auto' : 160,
            minHeight: isMobile ? 50 : 'auto',
            boxShadow: '0 4px 8px rgba(2, 132, 199, 0.3)',
            '&:hover': {
              bgcolor: '#0369a1',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 12px rgba(2, 132, 199, 0.4)'
            },
            '&:disabled': {
              bgcolor: '#d1d5db',
              color: '#9ca3af',
              boxShadow: 'none'
            },
            transition: 'all 0.2s ease'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: 'white' }} />
              {comparisonMode ? 'Applying...' : editData ? 'Updating...' : 'Creating...'}
            </Box>
          ) : (
            comparisonMode ? 'Add Scenario' : editData ? 'Update Security' : 'Create Security'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
